import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { supabase, isSupabaseConfigured } from '@/lib/supabase';
import { useToast } from '@/components/ui/toast-context';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

const schema = z.object({
  email: z.string().email(),
  password: z.string().min(8),
});
type FormValues = z.infer<typeof schema>;

export function Login() {
  const { t } = useTranslation(['auth', 'common']);
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [magicSent, setMagicSent] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  const onSubmit = handleSubmit(async (values) => {
    if (!isSupabaseConfigured()) {
      toast({ title: t('common:errors.generic'), description: 'Supabase not configured.', variant: 'destructive' });
      return;
    }
    setSubmitting(true);
    const { error } = await supabase.auth.signInWithPassword(values);
    setSubmitting(false);
    if (error) {
      toast({
        title: t('auth:errors.invalid_credentials'),
        description: error.message,
        variant: 'destructive',
      });
      return;
    }
    const from = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname ?? '/trainer';
    navigate(from, { replace: true });
  });

  const sendMagicLink = async () => {
    const email = getValues('email');
    const parsed = z.string().email().safeParse(email);
    if (!parsed.success) {
      toast({ title: t('common:errors.validation'), variant: 'destructive' });
      return;
    }
    if (!isSupabaseConfigured()) {
      toast({ title: t('common:errors.generic'), variant: 'destructive' });
      return;
    }
    const { error } = await supabase.auth.signInWithOtp({ email: parsed.data });
    if (error) {
      toast({ title: error.message, variant: 'destructive' });
      return;
    }
    setMagicSent(parsed.data);
  };

  if (magicSent) {
    return (
      <div className="mx-auto max-w-md p-6 pt-16">
        <Card>
          <CardHeader>
            <CardTitle>{t('auth:magic_link_sent.title')}</CardTitle>
            <CardDescription>{t('auth:magic_link_sent.body', { email: magicSent })}</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-md p-6 pt-16">
      <Card>
        <CardHeader>
          <CardTitle>{t('auth:login.title')}</CardTitle>
          <CardDescription>{t('auth:login.subtitle')}</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">{t('auth:login.email')}</Label>
              <Input id="email" type="email" autoComplete="email" {...register('email')} />
              {errors.email ? (
                <p className="text-xs text-destructive">{t('common:errors.validation')}</p>
              ) : null}
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">{t('auth:login.password')}</Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                {...register('password')}
              />
              {errors.password ? (
                <p className="text-xs text-destructive">{t('common:errors.validation')}</p>
              ) : null}
            </div>
            <Button type="submit" className="w-full" disabled={submitting}>
              {submitting ? t('common:states.loading') : t('auth:login.submit')}
            </Button>
            <Button type="button" variant="outline" className="w-full" onClick={sendMagicLink}>
              {t('auth:login.magic_link')}
            </Button>
          </form>
          <p className="mt-6 text-center text-xs text-muted-foreground">
            {t('auth:login.request_access')}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
